import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EPackageTiers } from '@feathr/blackbox';
import { Nav, NavItem, NavMenu } from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import EventSwitcher from '@feathr/extender/components/EventSwitcher';
import { useLicense, useMinPackageTooltip } from '@feathr/extender/hooks';
import { useLocalUrl } from '@feathr/extender/state';
import { getEventIdFromUrl } from '@feathr/extender/utils';

import EventContentNav from './EventContentNav';
import EventMarketingNav from './EventMarketingNav';
import EventPartnersNav from './EventPartnersNav';
import EventSettingsNav from './EventSettingsNav';

import styles from './EventNav.css';
interface ITooltipMessages {
  flights?: string;
  partners?: string;
}

type TTooltipByFeatureReturn = ITooltipMessages | undefined;

interface IEventNavProps {
  indented?: boolean;
}

function EventNav({ indented = true }: Readonly<IEventNavProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const eventId = getEventIdFromUrl();
  const { t } = useTranslation();
  const { invites, flights: hasFlights } = useLicense(['invites', 'flights']);

  const partnersTooltip = useMinPackageTooltip('partnerManager', 'invites');

  const tooltipsByFeature: TTooltipByFeatureReturn = useMemo(
    () => ({
      partners: invites ? undefined : partnersTooltip,
    }),
    [invites, partnersTooltip],
  );

  return (
    <>
      <EventSwitcher className={styles.select} />
      {!!eventId && (
        <Nav indented={indented} label={'Project navigation'} type={'vertical'}>
          <NavMenu
            data-command={'true'}
            data-command-description={t('Manage marketing campaigns')}
            data-command-keywords={'marketing,campaigns,ads,email'}
            data-command-label={t('Marketing')}
            hasDefaultChild={true}
            menu={<EventMarketingNav />}
            to={localUrl(`/projects/${eventId}/campaigns`)}
          >
            {t('Marketing')}
          </NavMenu>
          <TrackBlocked
            featureName={'flights'}
            locked={!hasFlights}
            requiredPackage={hasFlights ? undefined : EPackageTiers.Advanced}
          >
            <NavItem
              data-command={'true'}
              data-command-description={t('Manage campaign flights and scheduling')}
              data-command-keywords={'flights,scheduling,timing'}
              data-command-label={t('Flights')}
              to={localUrl(`/projects/${eventId}/flights`)}
              tooltip={tooltipsByFeature?.flights}
            >
              {t('Flights')}
            </NavItem>
          </TrackBlocked>
          {!invites ? (
            <TrackBlocked
              featureName={'partners'}
              locked={!invites}
              requiredPackage={invites ? undefined : EPackageTiers.Essential}
            >
              <NavItem
                data-command={'true'}
                data-command-description={t('Manage project partners')}
                data-command-keywords={'partners,collaboration,team'}
                data-command-label={t('Partners')}
                to={localUrl(`/projects/${eventId}/partners`)}
                tooltip={tooltipsByFeature?.partners}
              >
                {t('Partners')}
              </NavItem>
            </TrackBlocked>
          ) : (
            <NavMenu
              data-command={'true'}
              data-command-description={t('Manage project partners')}
              data-command-keywords={'partners,collaboration,team'}
              data-command-label={t('Partners')}
              menu={<EventPartnersNav />}
              to={localUrl(`/projects/${eventId}/partners`)}
            >
              {t('Partners')}
            </NavMenu>
          )}
          <NavMenu
            data-command={'true'}
            data-command-description={t('Manage content, forms, and templates')}
            data-command-keywords={'content,forms,templates,creatives'}
            data-command-label={t('Content')}
            menu={<EventContentNav />}
            to={localUrl(`/projects/${eventId}/content`)}
          >
            {t('Content')}
          </NavMenu>
          <NavItem
            data-command={'true'}
            data-command-description={t('View project analytics and reports')}
            data-command-keywords={'report,analytics,stats,metrics'}
            data-command-label={t('Project Report')}
            to={localUrl(`/projects/${eventId}/report`)}
          >
            {t('Report')}
          </NavItem>
          <NavMenu
            data-command={'true'}
            data-command-description={t('Configure project settings')}
            data-command-keywords={'settings,configuration,project'}
            data-command-label={t('Project Settings')}
            menu={<EventSettingsNav />}
            to={localUrl(`/projects/${eventId}/settings`)}
          >
            {t('Project Settings')}
          </NavMenu>
        </Nav>
      )}
    </>
  );
}

export default observer(EventNav);
